import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {DomSanitizer} from '@angular/platform-browser';
import {Subscription} from 'rxjs';
import {ParticipationFlowStore} from '@shared/participants/participation-flow.store';
import {
  Blob,
  BlockType,
  DateInput,
  DateInputSettings,
  InputType,
  Participant,
  ParticipationFlowNode,
  SignatureInput,
  SignatureInputValue,
  SubmitEventValue,
  TextInput,
  TextInputSettings
} from '@paperlessio/sdk/api/models';
import {SignatureInputModalComponent} from '@shared/signature-input-modal/signature-input-modal.component';
import {BLOCKS_CONFIG} from '../../blocks.config';
import {ControlContainer, FormControl, NgForm, ValidationErrors} from '@angular/forms';
import {SessionControllerService} from '../../../submission/src/app/session/session-controller.service';
import {BlockComponent} from '@blocks/block/block.component';
import {ContentLocale, ToastService} from '@paperlessio/sdk/api/util';
import {BaseModalService} from '@paperlessio/sdk/modal/base-modal';
import {filter, take} from 'rxjs/operators';
import {PdfPartComponent} from '@blocks/pdf-part/pdf-part.component';
import {SessionStore} from '../../../submission/src/app/session/session.store';
import {SubmissionSignatureBlobStorage} from '@shared/signature-input-modal/submission-signature-blob-storage';
import {SignatureInputData} from '@shared/signature-input-modal/signature-input-data';
import {base64toBlob} from '@paperlessio/sdk/util/helpers';
import {UploadService} from '@shared/services/upload.service';
import * as uuid from 'uuid';
import {TranslocoService} from '@ngneat/transloco';
import {LocalizationService} from '@shared/i18n/localization.service';

type SignatureInputOptionalInputs = {
  location?: TextInput,
  date?: DateInput,
  custom_value_1?: TextInput,
  custom_value_2?: TextInput,
  custom_value_3?: TextInput
}

@Component({
  selector: 'app-signature-input',
  templateUrl: './signature-input.component.html',
  styleUrls: ['./signature-input.component.scss'],
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}],
})
export class SignatureInputComponent implements OnInit, OnChanges, OnDestroy {
  public sanitizer = inject(DomSanitizer, {});
  public blocksConfig = inject(BLOCKS_CONFIG, {});
  public blockComponent = inject(BlockComponent, {optional: true});
  public elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  private toast = inject(ToastService, {});
  private form = inject(NgForm, {});
  private cdr = inject(ChangeDetectorRef, {});
  private translocoService = inject(TranslocoService);
  private baseModalService = inject(BaseModalService, {optional: true});
  private sessionController = inject(SessionControllerService, {optional: true});
  private sessionStore = inject(SessionStore, {optional: true});
  private participationFlowStore = inject(ParticipationFlowStore, {optional: true});
  private pdfPartComponent = inject(PdfPartComponent, {optional: true});
  private uploadService = inject(UploadService);
  private localizationService = inject(LocalizationService);
  private submissionSignatureBlobStorage = new SubmissionSignatureBlobStorage();

  @Input() block: SignatureInput;
  @Input() slug: string;
  @Input() data: SignatureInputValue;
  @Output() dataChange: EventEmitter<SignatureInputValue> = new EventEmitter<SignatureInputValue>();

  @ViewChild('signatureBox') signatureBox: ElementRef<HTMLElement>;

  public submitEventValue: SubmitEventValue;
  public domId = '';
  public subs = new Subscription();
  public formControl: FormControl<SignatureInputValue>;
  public ineligible = false;
  public today = new Date();
  public localImage?: string;

  public participantNodes: ParticipationFlowNode[] = [];
  private participant: Participant;

  private optionBlockCache: SignatureInputOptionalInputs = {};

  public get isOverlay(): boolean {
    return !!this.pdfPartComponent;
  }

  ngOnInit() {
    if (this.sessionStore != null) {
      this.submitEventValue = this.sessionStore.getAggregatedSubmissionData(this.slug);
      this.subs.add(this.sessionStore.submissionMetaInformation
        .pipe(filter(v => !!v))
        .subscribe(smi => this.participant = smi.participant));
    }

    this.formControl = new FormControl<SignatureInputValue>(this.data, () => this._valid());
    if (this.blocksConfig.submittable || this.sessionController) {
      this.domId = 'r' + (this.blocksConfig.editable ? 'e' : '') + (this.block.settings.editable ? 'q' : '');
      this.ineligible = this.sessionController && !this.sessionController.isParticipantAnOwnerOf(this.block.id);
    }

    // we have to disable manually to prevent invalid coloring
    if (this.ineligible) {
      this.formControl.disable();
    }

    this.form.form.addControl(this.slug, this.formControl);

    if (this.participationFlowStore) {
      this.subs.add(
        this.participationFlowStore.participantNodes.subscribe(
          nodes => {
            this.participantNodes = nodes;
            this.cdr.markForCheck();
          }
        )
      );
    }

    // reset cache on block change
    this.subs.add(this.blockComponent.blockChanged.subscribe(_ => {this.optionBlockCache = {};}));
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if slug has changed, we need to update the form control
    if (changes.slug && this.formControl) {
      this.form.form.removeControl(changes.slug.previousValue);
      this.form.form.addControl(changes.slug.currentValue, this.formControl);
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.form.form.removeControl(this.slug);
  }

  public isSigned(): boolean {
    return this.data?.signature_image_signed_id?.length > 0;
  }

  public isSignedDeprecated(): boolean {
    // dirty quickfix: empty svg file is about 220 chars long
    const signature_image = (this.data as any)?.signature_image;
    return signature_image?.length > 240 && signature_image?.startsWith('data:image/svg+xml;base64,');
  }

  public onSign(): void {
    if (this.ineligible) {
      return;
    }

    /**
     * This if-clause will handle the following two cases:
     *
     *  1. The user is currently filling in a Document with multiple signature inputs and has already signed once.
     *     In that case, the signedId from the previous signatures blob (from the same document) is used
     *  2. The user signed in another document but is signing a different document within the 5-minute timeout
     *     In this case we cant reuse the signedId from the signature of the other document. We need to re-upload the signature image
     *     because the signedId is scoped to the exact participant entity (which will be different from the previous one)
     *
     * In both cases the signature is placed directly into the input without opening the signature input modal.
     *
     * The signature input modal can be opened by clicking on the filled in signature input.
     */
    if (!this.data.signature_image_signed_id && this.participant?.email && this.participant?.id) {
      // Find the previous signature blobs corresponding 1. to the participant email and 2. to the participant id.
      const previousSignatures = this.submissionSignatureBlobStorage.getSignatureEntries(this.participant.email);
      const previousSignature = previousSignatures?.find(entry => entry.participantId === this.participant.id);

      // There's a previous signature from this participant. Set the data for it and don't open the input modal.
      if (previousSignature) {
        this.setData(previousSignature.data, true);
        return;
      }

      // There's a previous signature from this participant but from another document (email matches but not the id).
      // We need to re-upload it because the previous signedId is scoped to the other participant entity
      if (previousSignatures?.length > 0 && !previousSignature) {
        // We need to separate the encoded image from the mime-type so the image can be properly re-uploaded
        const dataParts = previousSignatures[0].data.signature_image.split(',');
        const mimeType = dataParts[0].replace('data:', '').replace(';base64', '');
        const filename = 'signature' + (mimeType.includes('png') ? '.png' : '.svg');

        try {
          const nativeBlob = base64toBlob(dataParts[1], mimeType);
          const file = new File([nativeBlob], filename, {type: mimeType});
          const blob = Blob.forUpload(file, uuid.v4());

          if (dataParts.length === 2 && mimeType.length > 3 && nativeBlob != null) {
            this.subs.add(this.uploadService
              .upload(blob)
              .subscribe(blob => {
                this.setData({
                  signature_image_signed_id: blob.signed_id,
                  signature_image: previousSignatures[0].data.signature_image,
                  signature_type: previousSignatures[0].data.signature_type,
                  signature_text: previousSignatures[0].data.signature_text
                });
              }));

            return;
          }
        } catch (e) {
          // No worries, just continue with the regular code
        }
      }
    }

    /**
     * From here on it's the regular signing process. Open the modal & set the data once the modal is closed.
     */
    if (this.blocksConfig.submittable && this.baseModalService) {
      this.baseModalService.open(
        SignatureInputModalComponent,
        {signature_type: this.data?.signature_type, local_image: this.localImage, signature_text: this.data?.signature_text},
        {panelClass: ['modal-lg', 'modal-sm-fullscreen', 'modal-dialog-scrollable'], ignoreBackdropClick: true}
      );

      this.subs.add(this.baseModalService.closing
        .pipe(take(1))
        .subscribe(value => {
          if (value?.signature_image_signed_id && value?.signature_image && value?.signature_type) {
            this.setData(value);
          } else {
            this.focusFirstInput();
          }
        }));
    }
  }

  private setData(value: SignatureInputData, signatureFromStore = false) {
    if (this.participant?.email && this.participant?.id && !signatureFromStore) {
      this.submissionSignatureBlobStorage
        .addSignature(this.participant.email, this.participant.id, {
          signature_image_signed_id: value.signature_image_signed_id,
          signature_image: value.signature_image,
          signature_text: value.signature_text ? value.signature_text : undefined,
          signature_type: value?.signature_type
        });
    }

    this.localImage = value.signature_image;
    this.data.signature_image_signed_id = value.signature_image_signed_id;
    this.data.signature_type = value.signature_type;
    this.data.signature_text = value.signature_text ? value.signature_text : undefined;
    this.data.date ??= new Date();
    this.dataChange.emit(this.data);
    this.formControl.markAsTouched();
    this.formControl.markAsDirty();
    this.formControl.setValue(this.data, {emitViewToModelChange: false});

    this.focusFirstInput();

    this.cdr.markForCheck();
  }

  /**
   * Focus first input element in the signature-input block if possible
   */
  private focusFirstInput() {
    this.elementRef.nativeElement?.querySelector<HTMLElement>('.form-control:first-of-type')?.focus();
  }

  public onChangeInput(key: string, value: string): void {
    this.data[key] = value;
    this.dataChange.emit(this.data);
  }

  public onClickSignatureBoxInEditor(): void {
    if(this.blockComponent?.selected) {
      this.toast.info('blocks.' + this.block.type + '.signing_deactivated');
    }
  }

  public onClickDateInput(): void {
    if(!this.blocksConfig.editable || this.blockComponent?.selected) {
      this.toast.info('blocks.' + this.block.type + '.date_deactivated');
    }
  }

  public getTemporarySlug(key: string): string {
    return '@excluded-slug-' + this.slug + '-' + key;
  }

  public getOptionalInput(key: keyof SignatureInputOptionalInputs): SignatureInputOptionalInputs[keyof SignatureInputOptionalInputs] {
    if (!this.optionBlockCache[key]) {
      let block;
      switch (key) {
        case 'location':
        case 'custom_value_1':
        case 'custom_value_2':
        case 'custom_value_3':
          let name;

          if (this.block.settings[key + '_label']) {
            name = this.block.settings[key + '_label'];
          } else if (key === 'location') {
            name = this.translocoService.translate('blocks.Block::Input::SignatureInput.settings.location');
          } else {
            name = this.translocoService.translate('controls.options_list.option.new_option_placeholder');
          }

          block = new TextInput({
            slug: this.getTemporarySlug(key),
            id: this.block.id,
            type: BlockType.TextInput,
            parent: this.block,
            settings: {
              type: InputType.text,
              readonly: false,
              hide_name: true,
              padding: true,
              editable: null,
              required: this.block.settings.required,
              name: name
            } as TextInputSettings
          });
          break;
        case 'date':
          block = new DateInput({
            slug: this.getTemporarySlug(key),
            id: this.block.id,
            type: BlockType.DateInput,
            parent: this.block,
            settings: {
              readonly: this.blocksConfig.editable,
              hide_name: true,
              editable: null,
              required: this.block.settings.required,
              today: false,
              name: this.block.settings[key + '_label'] ? this.block.settings[key + '_label']: this.translocoService.translate('blocks.Block::Input::SignatureInput.settings.date')
            } as DateInputSettings
          });
      }
      this.optionBlockCache[key] = block;
    }

    return this.optionBlockCache[key];
  }

  public get fieldOrderForPrintMode(): string {
    const itemNames = this.block.settings.options_order
      .filter(option => this.block.settings['show_' + option])
      .map(option => {
        if (this.block.settings[option + '_label']) {
          return this.block.settings[option + '_label'];
        } else if (option === 'location') {
          return this.translocoService.translate('blocks.Block::Input::SignatureInput.settings.location');
        } else if (option === 'date') {
          return this.translocoService.translate('blocks.Block::Input::SignatureInput.settings.date');
        } else {
          return '';
        }
      })
      .filter(label => label?.length > 0);

    itemNames.push(this.translocoService.translate('blocks.Block::Input::SignatureInput.title'));

    const ownerNames = this.owners
      .map(owner => owner.participant?.name || owner.participant?.slot_name)
      .filter(name => name?.length > 0);

    return itemNames.join(', ') + ' ' + ownerNames.join(', ');
  }

  get currentContentLocale(): ContentLocale {
    return this.localizationService.currentContentLocale;
  }

  get originalContentLocale(): ContentLocale {
    return this.localizationService.originalContentLocale;
  }

  private _valid(): ValidationErrors | null {
    return !this.block.settings.required || (this.isSigned() || this.isSignedDeprecated()) ? null : {required: true};
  }

  get owners(): ParticipationFlowNode[] {
    return this.participantNodes.filter(
      n => this.block.block_owner_participant_ids.includes(n.participant?.id)
    );
  }
}
